import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleRight } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import BadgeSlash from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/BadgeSlash';
import HitsCarousel from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/HitsCarousel';
import LinkTag from 'Components/Carousel/LinkTag';
import PlatformIcons from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/PlatformIcons';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import PrimaryMessage from 'Components/Carousel/PrimaryMessage';
import QuickLook from 'Components/Cards/HitCardQuickLook';
import SecondaryMessage from 'Components/Carousel/SecondaryMessage';
import useViewport from 'Hooks/useViewport';
import WishlistButton from 'Components/Wishlist/WishlistButton';

export default function BundlePanel({
  hit,
  slide,
  slideClickFunction,
  slideExternalLink,
  slideUrl,
  switchCoverImg,
}) {
  const { isAboveMd, isAboveXl, isBelowLg, isBelowXl, isXl } = useViewport();

  let discountPercent = hit.discount_percent;

  if (hit.type === 'pick-and-mix') {
    discountPercent = hit.pnm_saving;
  }

  let validDrm = '';
  let filteredDrm = [];
  if (hit && hit.drm) filteredDrm = hit.drm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  const timeSinceLaunch = (Date.now() / 1000) - hit.available_valid_from;
  let badgeType = 'bundle';
  if (timeSinceLaunch < 86400) badgeType = 'justLaunched';
  if (hit.display_type === 'game') badgeType = 'product';
  if (hit.is_srp_bundle) badgeType = 'product';

  const showMessageBlock = slide.primary_message
    || slide.primary_message_type !== 'none'
    || slide.secondary_message
    || slide.secondary_message_type !== 'none';

  return (
    <div className="BundlePanel CarouselPanel">
      <div className="CarouselPanel__topContainer">
        <div className="CarouselPanel__BadgeWishlistContainer">
          <BadgeSlash type={badgeType} releaseDate={hit.release_date} />
          {hit.type !== 'pick-and-mix' &&
            <WishlistButton
              noText={window.locale !== 'en' && isBelowXl}
              reducedText={window.locale !== 'en'}
              slug={hit.slug}
              productId={hit.product_id}
              product={hit}
            />
          }
        </div>

        <h2 className="CarouselPanel__title">
          <LinkTag
            url={slideUrl}
            externalLink={slideExternalLink}
            className="faux-block-link__overlay-link"
            onClick={() => slideClickFunction()}
          >
            {hit.name}
          </LinkTag>
        </h2>

        {(isAboveXl || (isXl && (hit.name && hit.name.length < 20))) &&
          <React.Fragment>
            {!hit.mystery &&
              <div className="CarouselPanel__quickLook">
                <QuickLook featuredCarousel hit={hit} />
              </div>
            }

            <HitsCarousel
              autoPlay
              hits={slide.hit.bundle_covers}
              switchCoverImg={switchCoverImg}
              type={hit.type}
            />
          </React.Fragment>
        }

        {(hit.mystery || (isXl && (hit.name && hit.name.length >= 20))) &&
          <div className="CarouselPanel__shortDesc CarouselPanel__section">
            {(hit.sdescs ? hit.sdescs[(window.locale || 'en')] : null) || hit.sdesc}
          </div>
        }

        {showMessageBlock &&
          <div className="CarouselPanel__messageBlock CarouselPanel__section">
            <PrimaryMessage
              customMessage={slide.primary_message}
              messageType={slide.primary_message_type}
              messageData={slide.promotional_message_data}
            />
            <SecondaryMessage
              customMessage={slide.secondary_message}
              messageType={slide.secondary_message_type}
              messageData={slide.promotional_message_data}
            />
          </div>
        }
      </div>

      <div className="CarouselPanel__bottomContainer">
        <PlatformIcons hit={hit} />
        <div className="CarouselPanel__bottomContainer__priceButton">
          {!hit.presale && !hit.free_to_play && !hit.hide_price &&
            <PriceContainer
              type={hit.type}
              discountPercent={discountPercent}
              price={hit.price}
              tiered={hit.tiered}
              hideDiscount={hit.hide_discount}
              slug={hit.slug}
              discountPercentOverride={hit.discount_percent_override}
              fullPrice={hit.fullPrice}
              mystery={hit.mystery}
              drm={validDrm}
              payWhatYouWant={hit.pay_what_you_want}
            />
          }
          {!hit.tiered && !hit.presale && !hit.free_to_play && !hit.pay_what_you_want && hit.type !== 'pick-and-mix' &&
            <AddToCartBtn
              showInCartLink
              id={hit.product_id}
              slug={hit.slug}
              voucherToAdd={hit.addVoucher}
              hideButtonText={window.locale !== 'en' && isAboveMd}
            />
          }

          {(hit.tiered || hit.pay_what_you_want || hit.type === 'pick-and-mix') &&
            <LinkTag
              url={slideUrl}
              externalLink={slideExternalLink}
              className={classNames('AddToCartBtn btn btn-primary', { 'AddToCartBtn--iconOnly': window.locale !== 'en' && isAboveMd })}
              onClick={() => slideClickFunction()}
            >
              <div className="cart-btn-icon">
                <FontAwesomeIcon icon={faCircleRight} />
              </div>
              {(window.locale === 'en' || (window.locale !== 'en' && isBelowLg)) &&
                <div className="btn-text-container">
                  <FormattedMessage id="bundle-panel.button.view" defaultMessage="View" />
                </div>
              }
            </LinkTag>
          }
        </div>
      </div>
    </div>
  );
}

BundlePanel.propTypes = {
  hit: PropTypes.shape({}),
  slide: PropTypes.shape({}),
  slideExternalLink: PropTypes.bool,
  slideUrl: PropTypes.string,
  switchCoverImg: PropTypes.func,
  slideClickFunction: PropTypes.func,
};

BundlePanel.defaultProps = {
  hit: {},
  slide: {},
  slideExternalLink: false,
  slideUrl: '',
  switchCoverImg: () => { },
  slideClickFunction: () => { },
};
